import { ChevronDown, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../ui/select';
import { Switch } from '../../ui/switch';
import type { InputNode } from '../../documents/types';

type FieldType = 'text' | 'number' | 'textarea' | 'switch' | 'select' | 'toggle-group';

interface FieldDef {
    id: string;
    label: string;
    type: FieldType;
    placeholder?: string;
    options?: { label: string; value: string }[];
    showWhen?: { field: string; eq: any };
    style?: { fullWidth?: boolean };
}

interface ArrayFieldDef {
    id: string;
    label: string;
    addLabel: string;
    fields: FieldDef[];
}

interface SectionDef {
    id: string;
    label: string;
    key?: string;
    defaultOpen?: boolean;
    fields?: FieldDef[];
    arrayField?: ArrayFieldDef;
}

const matchesShowWhen = (
    sectionValue: Record<string, any> | undefined,
    showWhen?: FieldDef['showWhen']
) => {
    if (!showWhen) return true;

    const fieldValue = sectionValue?.[showWhen.field];

    if (showWhen.eq === false) {
        return fieldValue !== true;
    }

    return fieldValue === showWhen.eq;
};

export interface ConditionalsSchemaConfig {
    sections: SectionDef[];
}

interface SchemaConditionalsRendererProps {
    value: any;
    onChange: (value: any) => void;
    disabled?: boolean;
    field: InputNode;
}

const createId = () =>
    typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

function CollapsibleSection({
    id,
    title,
    isOpen,
    onToggle,
    children,
}: {
    id: string;
    title: string;
    isOpen: boolean;
    onToggle: (id: string) => void;
    children: React.ReactNode;
}) {
    return (
        <section className="rounded-lg border border-subtle bg-white/[0.03]">
            <button
                type="button"
                onClick={() => onToggle(id)}
                className="flex w-full items-center justify-between px-4 py-3 text-left"
            >
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                <ChevronDown
                    className={`h-4 w-4 text-muted-foreground transition-transform ${
                        isOpen ? 'rotate-180' : ''
                    }`}
                />
            </button>
            {isOpen && <div className="space-y-4 px-4 pb-4">{children}</div>}
        </section>
    );
}

function FieldRenderer({
    def,
    value,
    onChange,
    disabled,
}: {
    def: FieldDef;
    value: any;
    onChange: (value: any) => void;
    disabled: boolean;
}) {
    const label = def.label ? (
        <Label className="text-sm font-medium text-foreground">{def.label}</Label>
    ) : null;

    switch (def.type) {
        case 'textarea':
            return (
                <div className="space-y-1.5">
                    {label}
                    <textarea
                        value={value ?? ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={def.placeholder}
                        disabled={disabled}
                        className="flex min-h-[88px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-subtle-foreground/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                </div>
            );

        case 'number':
            return (
                <div className="space-y-1.5">
                    {label}
                    <Input
                        type="number"
                        value={value ?? ''}
                        onChange={(e) =>
                            onChange(e.target.value === '' ? undefined : Number(e.target.value))
                        }
                        placeholder={def.placeholder}
                        disabled={disabled}
                    />
                </div>
            );

        case 'text':
            return (
                <div className="space-y-1.5">
                    {label}
                    <Input
                        value={value ?? ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={def.placeholder}
                        disabled={disabled}
                    />
                </div>
            );

        case 'switch':
            return (
                <div className="flex items-center gap-3">
                    <Switch
                        checked={!!value}
                        onCheckedChange={onChange}
                        disabled={disabled}
                    />
                    {label}
                </div>
            );

        case 'select':
            return (
                <div className="space-y-1.5">
                    {label}
                    <Select value={value ?? ''} onValueChange={onChange} disabled={disabled}>
                        <SelectTrigger className="bg-background">
                            <SelectValue placeholder={def.placeholder ?? def.label} />
                        </SelectTrigger>
                        <SelectContent>
                            {(def.options ?? []).map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            );

        case 'toggle-group': {
            const arr = Array.isArray(value) ? value : [];
            return (
                <div className="space-y-1.5">
                    {label}
                    <div className="flex flex-wrap gap-2">
                        {(def.options ?? []).map((opt) => {
                            const optValue = isNaN(Number(opt.value)) ? opt.value : Number(opt.value);
                            const selected = arr.includes(optValue);
                            return (
                                <Button
                                    key={opt.value}
                                    type="button"
                                    variant={selected ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() =>
                                        onChange(
                                            selected
                                                ? arr.filter((v: any) => v !== optValue)
                                                : [...arr, optValue]
                                        )
                                    }
                                    disabled={disabled}
                                >
                                    {opt.label}
                                </Button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        default:
            return null;
    }
}

function ArrayField({
    def,
    items,
    onChange,
    disabled,
}: {
    def: ArrayFieldDef;
    items: any[];
    onChange: (items: any[]) => void;
    disabled: boolean;
}) {
    const updateItem = (index: number, fieldId: string, fieldValue: any) => {
        const updated = [...items];
        updated[index] = { ...updated[index], [fieldId]: fieldValue };
        onChange(updated);
    };

    return (
        <div className="space-y-3">
            {items.map((item, index) => (
                <div
                    key={item.id ?? index}
                    className="flex items-center gap-3 rounded-lg border border-subtle/60 p-3"
                >
                    {def.fields.map((fieldDef) => (
                        <div key={fieldDef.id} className="flex-1 min-w-0">
                            <FieldRenderer
                                def={{
                                    ...fieldDef,
                                    label: '',
                                    placeholder: fieldDef.placeholder ?? fieldDef.label,
                                }}
                                value={item[fieldDef.id]}
                                onChange={(v) => updateItem(index, fieldDef.id, v)}
                                disabled={disabled}
                            />
                        </div>
                    ))}
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => onChange(items.filter((_, i) => i !== index))}
                        disabled={disabled}
                        className="shrink-0"
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>
            ))}
            <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onChange([...items, { id: createId() }])}
                disabled={disabled}
            >
                <Plus className="mr-2 h-4 w-4" />
                {def.addLabel}
            </Button>
        </div>
    );
}

function SectionFields({
    section,
    sectionValue,
    onFieldChange,
    disabled,
}: {
    section: SectionDef;
    sectionValue: any;
    onFieldChange: (fieldId: string, fieldValue: any) => void;
    disabled: boolean;
}) {
    const visibleFields = (section.fields ?? []).filter((f) =>
        matchesShowWhen(sectionValue, f.showWhen)
    );
    const isWaitingPeriodSection = section.id === 'waitingPeriod';

    if (!visibleFields.length && !section.arrayField) return null;

    return (
        <>
            {visibleFields.length > 0 && (
                <div
                    className={
                        isWaitingPeriodSection
                            ? 'space-y-4'
                            : 'grid grid-cols-1 gap-3 md:grid-cols-2'
                    }
                >
                    {visibleFields.map((fieldDef) => (
                        <div
                            key={fieldDef.id}
                            className={
                                isWaitingPeriodSection
                                    ? fieldDef.id === 'weeksAfterScan'
                                        ? 'max-w-sm'
                                        : ''
                                    : fieldDef.type === 'textarea' || fieldDef.style?.fullWidth
                                      ? 'md:col-span-2'
                                      : ''
                            }
                        >
                            <FieldRenderer
                                def={fieldDef}
                                value={sectionValue?.[fieldDef.id]}
                                onChange={(v) => onFieldChange(fieldDef.id, v)}
                                disabled={disabled}
                            />
                        </div>
                    ))}
                </div>
            )}
            {section.arrayField && (
                <ArrayField
                    def={section.arrayField}
                    items={sectionValue?.[section.arrayField.id] ?? []}
                    onChange={(v) => onFieldChange(section.arrayField!.id, v)}
                    disabled={disabled}
                />
            )}
        </>
    );
}

export function SchemaConditionalsRenderer({
    value,
    onChange,
    disabled = false,
    field,
}: SchemaConditionalsRendererProps) {
    const { t } = useTranslation();
    const config = field.data?.default?.fixed as ConditionalsSchemaConfig | undefined;
    const sections = config?.sections ?? [];

    const [openSections, setOpenSections] = useState<string[]>(
        sections.filter((s) => s.defaultOpen).map((s) => s.id)
    );

    const currentValue = value ?? {};

    const toggleSection = (sectionId: string) => {
        setOpenSections((current) =>
            current.includes(sectionId)
                ? current.filter((id) => id !== sectionId)
                : [...current, sectionId]
        );
    };

    const handleFieldChange = (section: SectionDef, fieldId: string, fieldValue: any) => {
        if (section.key) {
            onChange({
                ...currentValue,
                [section.key]: {
                    ...(currentValue[section.key] ?? {}),
                    [fieldId]: fieldValue,
                },
            });
        } else {
            onChange({ ...currentValue, [fieldId]: fieldValue });
        }
    };

    if (!sections.length) {
        return (
            <div className="text-sm text-muted-foreground">
                {t('departmentPolicies.conditionals.noSchema')}
            </div>
        );
    }

    const getSectionValue = (section: SectionDef) =>
        section.key ? currentValue[section.key] : currentValue;

    return (
        <div className="space-y-4">
            {sections.map((section) => (
                <CollapsibleSection
                    key={section.id}
                    id={section.id}
                    title={section.label}
                    isOpen={openSections.includes(section.id)}
                    onToggle={toggleSection}
                >
                    <SectionFields
                        section={section}
                        sectionValue={getSectionValue(section)}
                        onFieldChange={(fieldId, fieldValue) =>
                            handleFieldChange(section, fieldId, fieldValue)
                        }
                        disabled={disabled}
                    />
                </CollapsibleSection>
            ))}
        </div>
    );
}
